import { Segmented, Select } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    MessageTemplate,
    MessageTemplatePurpose,
    messageTemplatePurposes,
    MessageTemplateQuery,
} from '../message-template'

export type TemplateSortValue = NonNullable<MessageTemplateQuery['sort']>

export const defaultTemplateSort: TemplateSortValue = '-created_at'

// Maps a single sort control onto the table-shaped onChange handler that
// usePagination expects, so the reading list keeps sort without column headers.
const sortOptions: { value: TemplateSortValue; field: keyof MessageTemplate; order: 'ascend' | 'descend' }[] = [
    { value: '-created_at', field: 'createdAt', order: 'descend' },
    { value: 'created_at', field: 'createdAt', order: 'ascend' },
    { value: 'title', field: 'title', order: 'ascend' },
    { value: 'status', field: 'status', order: 'ascend' },
]

export function buildTemplateSorter(value: string): SorterResult<MessageTemplate> {
    const option = sortOptions.find((candidate) => candidate.value === value) ?? sortOptions[0]
    return { field: option.field, order: option.order } as SorterResult<MessageTemplate>
}

type MessageTemplateSortProps = {
    value: TemplateSortValue
    onChange: (value: TemplateSortValue) => void
}

export function MessageTemplateSort({ value, onChange }: MessageTemplateSortProps) {
    const { t } = useTranslation()

    const sortLabels: Record<string, string> = {
        '-created_at': t('Newest first'),
        created_at: t('Oldest first'),
        title: t('Title'),
        status: t('Status'),
    }

    return (
        <Select
            value={value}
            onChange={onChange}
            style={{ width: 150 }}
            options={sortOptions.map(({ value }) => ({ value, label: sortLabels[value] }))}
        />
    )
}

type MessageTemplateVisibilityFilterProps = {
    value: 'all' | 'public' | 'private'
    onChange: (value: 'all' | 'public' | 'private') => void
}

export function MessageTemplateVisibilityFilter({ value, onChange }: MessageTemplateVisibilityFilterProps) {
    const { t } = useTranslation()

    return (
        <Select
            value={value}
            onChange={onChange}
            style={{ width: 185 }}
            options={[
                { value: 'all', label: t('Public and private') },
                { value: 'public', label: t('Public') },
                { value: 'private', label: t('Private') },
            ]}
        />
    )
}

export type TemplateStatusFilterValue = 'all' | MessageTemplate['status']

type MessageTemplateStatusFilterProps = {
    value: TemplateStatusFilterValue
    onChange: (value: TemplateStatusFilterValue) => void
}

export function MessageTemplateStatusFilter({ value, onChange }: MessageTemplateStatusFilterProps) {
    const { t } = useTranslation()

    return (
        <Select
            value={value}
            onChange={onChange}
            style={{ width: 150 }}
            options={[
                { value: 'all', label: t('All statuses') },
                { value: 'APPROVED', label: t('templateStatuses.APPROVED') },
                { value: 'PENDING', label: t('templateStatuses.PENDING') },
                { value: 'REJECTED', label: t('templateStatuses.REJECTED') },
            ]}
        />
    )
}

export type TemplatePurposeFilterValue = 'all' | MessageTemplatePurpose

type MessageTemplatePurposeFilterProps = {
    value: TemplatePurposeFilterValue
    counts: Record<TemplatePurposeFilterValue, number>
    onChange: (value: TemplatePurposeFilterValue) => void
}

// The primary way through a growing template list: by the moment it's sent.
export function MessageTemplatePurposeFilter({ value, counts, onChange }: MessageTemplatePurposeFilterProps) {
    const { t } = useTranslation()

    const option = (purpose: TemplatePurposeFilterValue, label: string) => ({
        value: purpose,
        label: (
            <span className="inline-flex items-center gap-1.5">
                {label}
                <span className="text-xs tabular-nums text-ink-tertiary">{counts[purpose]}</span>
            </span>
        ),
    })

    return (
        <Segmented
            className="max-w-full overflow-x-auto [scrollbar-width:none]"
            value={value}
            onChange={onChange}
            options={[
                option('all', t('All')),
                ...messageTemplatePurposes.map((purpose) => option(purpose, t(`templatePurposes.${purpose}`))),
            ]}
        />
    )
}
